import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { SecondaryButton } from 'ui/button';
import { FormField, FormLabel } from 'ui/formField';
import { Confirm } from 'ui/icons/action/Confirm';
import { Handlebars } from 'ui/icons/action/Handlebars';
import { colors, monospace2, spacing, utilityColors } from 'ui/theme';
import { CopyToClipboard } from '../CopyToClipboard';
import { useTranslations } from '../../hooks/useTranslations';
import { SnippetFieldsInputIds } from '../../types';
import { generateSlug } from '../../utils';

const SnippetPreviewFieldSection = styled.div`
  background-color: ${colors.grey5};
  border-radius: ${spacing.half};
  padding: ${spacing.plusHalf};
`;

const SnippetCodeText = styled.div`
  border-radius: ${spacing.eighth};
  padding: ${spacing.quarter};
  user-select: none;
`;

const CopyToClipboardButton = styled(SecondaryButton)`
  align-self: flex-start;

  &:hover {
    & + * > * > span {
      background: ${colors.green10};
    }
  }
`;

type SnippetCodePreviewProps = {
  showCopyButton: boolean;
};

const SnippetCodePreview = styled.div<SnippetCodePreviewProps>`
  ${({ showCopyButton }) => (showCopyButton ? `margin-bottom: ${spacing.plusHalf}` : ``)};
  ${monospace2}
  background-color: ${colors.white};
  border: ${spacing.single} dashed ${colors.borderStrong};
  color: ${utilityColors.info100};
  padding: ${spacing.plusHalf};
  word-break: break-word;
  border-radius: ${spacing.half};
`;

const SnippetFormFieldContent = styled.div`
  display: flex;
  flex-direction: column-reverse;
`;

const StyledHandlebars = styled(Handlebars)`
  margin-right: ${spacing.threeQuarter};
`;

export const SnippetPreview = ({
  isCopyDisabled,
  isEditMode,
}: {
  isCopyDisabled: boolean;
  isEditMode: boolean;
}) => {
  const { t } = useTranslations();
  const { getValues } = useFormContext();
  const snippetName = getValues(SnippetFieldsInputIds.name);
  const positionalParameters = getValues(SnippetFieldsInputIds.positionalParameters);
  const [isCopied, setIsCopied] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout>>();

  const snippetCodePreview = generateSlug(
    snippetName.length > 0 ? snippetName : t('input.snippetName.placeholder'),
    positionalParameters,
  );

  // Show 'copied' indicator, then revert after 3 seconds
  const onCopy = useCallback(() => {
    if (copiedTimer.current) {
      clearTimeout(copiedTimer.current);
    }
    setIsCopied(true);
    copiedTimer.current = setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (copiedTimer.current) {
        clearTimeout(copiedTimer.current);
      }
    };
  }, []);

  return (
    <SnippetPreviewFieldSection>
      <FormField
        hasError={false}
        inputId="snippet-preview"
        labelText={
          <FormLabel>
            <StyledHandlebars />
            {t('label.snippetPreview')}
          </FormLabel>
        }
      >
        <SnippetFormFieldContent>
          {isEditMode && (
            <CopyToClipboard
              disabled={isCopyDisabled}
              disabledTooltip={t('tooltip.disabledCopy')}
              onCopy={onCopy}
              text={snippetCodePreview}
            >
              <CopyToClipboardButton isDisabled={isCopyDisabled}>
                {isCopied && <Confirm isColorInherited={true} />}
                <div>{isCopied ? t('button.copied') : t('button.copy')}</div>
              </CopyToClipboardButton>
            </CopyToClipboard>
          )}
          <SnippetCodePreview showCopyButton={isEditMode}>
            <SnippetCodeText>
              <span>{snippetCodePreview}</span>
            </SnippetCodeText>
          </SnippetCodePreview>
        </SnippetFormFieldContent>
      </FormField>
    </SnippetPreviewFieldSection>
  );
};
